import {Controller} from "@hotwired/stimulus"
import {Howl, Howler} from 'howler'
import {getQueryParam} from "../utils/query";
import { notifyParentOfWordHighlight } from "../utils/iframe_messaging";

export default class extends Controller {
  connect() {
    this.player = null;
    this.audioData = null;
    this.segmentsData = null;
    this.isPlaying = false;
    this.rafId = null;
    this.loading = false;
    this.segmentsLoaded = false;
    this.currentVerse = null;
    this.currentWord = null;
    this.currentReciterId = null;

    // DOM elements
    this.playButton = this.element.querySelector('#play-button');
    this.currentTimeEl = this.element.querySelector('#current-time');
    this.totalDurationEl = this.element.querySelector('#total-duration');
    this.progressBar = this.element.querySelector('#progress-bar');
    this.progressFill = this.element.querySelector('#progress-bar #progress');
    this.loadingIndicator = this.createLoadingIndicator();
    this.settingBtn = this.element.querySelector('#audio-settings');
    this.reciterSelect = this.element.querySelector('#reciter-select');

    this.currentTimeEl.classList.add('hidden');
    this.totalDurationEl.classList.add('hidden');

    const currentAyah = getQueryParam('ayah', 1)
    this.currentAyahNumber = currentAyah

    // Initialize
    this.currentReciterId = this.reciterSelect.value;
    this.setupEventListeners();
  }

  disconnect() {
    this.cleanUpPlayer();
    this.removeEventListeners();
    this.loadingIndicator?.remove();
  }

  playAyah(ayahNumber) {
    this.currentAyahNumber = ayahNumber;
    this.updatePlayButtonIcon();

    if (this.player) {
      const time = this.getAyahStartTimeInSec(this.currentAyahNumber);
      this.player.seek(time);
      if (!this.isPlaying) {
        this.togglePlay();
      }
    } else {
      this.togglePlay();
    }
  }

  // ========== UI Methods ==========
  createLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'absolute inset-0 flex items-center justify-center bg-white/80 z-10 hidden';
    indicator.innerHTML = `
      <svg class="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    `;
    this.element.appendChild(indicator);
    return indicator;
  }

  showLoading() {
    this.loading = true;
    this.loadingIndicator.classList.remove('hidden');
    this.playButton.setAttribute('disabled', 'disabled');
  }

  hideLoading() {
    this.loading = false;
    this.loadingIndicator.classList.add('hidden');
    this.playButton.removeAttribute('disabled');
  }

  updatePlayButtonIcon() {
    if (this.playButton) {
      const svgPath = this.playButton.querySelector('path');
      if (svgPath) {
        if (this.isPlaying) {
          svgPath.setAttribute('d', 'M6 19h4V5H6v14zm8-14v14h4V5h-4z');
        } else {
          svgPath.setAttribute('d', 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z');
        }
      }
    }


    if (this.currentAyahNumber) {
      document.querySelectorAll('.play-ayah i').forEach(icon => {
        icon.classList.remove('fa-pause');
        icon.classList.add('fa-play');
      });

      const currentAyahButton = document.querySelector(`[data-ayah$=":${this.currentAyahNumber}"] .play-ayah i`);

      if (currentAyahButton) {
        if (this.isPlaying) {
          currentAyahButton.classList.remove('fa-play');
          currentAyahButton.classList.add('fa-pause');
        } else {
          currentAyahButton.classList.remove('fa-pause');
          currentAyahButton.classList.add('fa-play');
        }
      }
    }
  }

  // ========== Event Handling ==========
  setupEventListeners() {
    this.playButton.addEventListener('click', this.togglePlay.bind(this));
    this.progressBar.addEventListener('click', this.seek.bind(this));
    this.settingBtn?.addEventListener('click', this.toggleSetting.bind(this));
    this.reciterSelect.addEventListener('change', this.changeReciter.bind(this));

    const speedSelect = document.getElementById('speed-select');
    if (speedSelect) {
      speedSelect.addEventListener('change', this.changeSpeed.bind(this));
    }

    $(document).on("click", ".play-ayah", (event) => {
      event.preventDefault();
      const ayahEl = $(event.currentTarget).closest('[data-ayah]');
      const key = ayahEl.data('ayah') || '';
      const ayahNumber = key.split(":")[1] || 1;

      if (this.currentAyahNumber === ayahNumber.toString() && this.player) {
        if (this.isPlaying) {
          this.pause();
        } else {
          this.player.play();
          this.isPlaying = true;
          this.updatePlayButtonIcon();
        }
      } else {
        this.playAyah(ayahNumber);
      }
    })
  }

  removeEventListeners() {
    this.playButton?.removeEventListener('click', this.togglePlay);
    this.progressBar?.removeEventListener('click', this.seek);
    this.settingBtn?.removeEventListener('click', this.toggleSetting);
    this.reciterSelect?.removeEventListener('change', this.changeReciter);

    const speedSelect = document.getElementById('speed-select');
    if (speedSelect) {
      speedSelect.removeEventListener('change', this.changeSpeed);
    }
  }

  toggleSetting() {
    const menu = document.querySelector('#audio-settings-menu');
    if (menu) {
      menu.classList.toggle('hidden');
    }
  }

  // ========== Speed Change Handling ==========
  changeSpeed(event) {
    const menu = document.querySelector('#audio-settings-menu');
    if (menu) {
      menu.classList.add('hidden');
    }

    if (this.player) {
      this.player.rate(parseFloat(event.target.value));
    }
  }

  updateProgress() {
    const currentTime = this.player.seek();
    this.currentTimeEl.textContent = this.formatTime(currentTime);

    const progressPercent = (currentTime / this.player.duration()) * 100;
    this.progressFill.style.width = `${progressPercent}%`;

    this.updateHighlighting(currentTime * 1000); // Convert to milliseconds

    this.rafId = requestAnimationFrame(() => this.updateProgress());
  }

  // ========== Player Control Methods ==========
  async togglePlay() {
    if (this.loading) return;

    if (!this.segmentsLoaded) {
      this.showLoading();
      try {
        const data = await this.fetchSegmentsData();
        this.segmentsData = data.segments;
        this.audioData = data.audio;
        this.segmentsLoaded = true;
      } catch (error) {
        console.error('Error loading segments:', error);
        return;
      } finally {
        this.hideLoading();
      }
    }

    if (!this.player) {
      this.initializeAudioPlayer();
    }

    if (this.player.playing()) {
      this.pause();
      return;
    }

    if (this.currentAyahNumber) {
      const time = this.getAyahStartTimeInSec(this.currentAyahNumber);
      this.player.seek(time);
    }

    this.player.play();
    this.isPlaying = true;
    this.updatePlayButtonIcon();

    // Start progress updates
    if (!this.rafId) {
      this.updateProgress();
    }
  }

  pause() {
    if (!this.player) return;

    this.player.pause();
    this.isPlaying = false;
    this.updatePlayButtonIcon();

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.element.dispatchEvent(
      new CustomEvent('quran-player-paused', {
        detail: {
          currentTime: this.player.seek(),
          currentVerse: this.currentVerse
        }
      })
    );
  }

  getAyahStartTimeInSec(ayahNumber) {
    const key = `${this.currentChapter()}:${ayahNumber}`;
    const seg = this.findSegmentByVerseKey(key);

    if (seg) {
      return seg.timestamp_from / 1000
    } else{
      return this.segmentsData[0].timestamp_from / 1000
    }
  }

  findSegmentByVerseKey(key) {
    return this.segmentsData.find(seg => seg.verse_key === key);
  }

  seek(event) {
    if (!this.player) return;

    const rect = this.progressBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const seconds = percent * this.player.duration();
    this.player.seek(seconds);
    const ayah = this.findAyah(this.player.seek() * 1000)
    this.currentVerse = ayah;
    this.highlightVerse(ayah)
    this.updateProgress();
  }

  // ========== Reciter Change Handling ==========
  async changeReciter(event) {
    const menu = document.querySelector('#audio-settings-menu');
    if (menu) {
      menu.classList.add('hidden');
    }

    const newReciterId = event.target.value;
    if (newReciterId == this.currentReciterId) return;

    this.currentReciterId = newReciterId;
    this.segmentsLoaded = false;

    // Store current state
    const wasPlaying = this.isPlaying;
    const currentVerse = this.currentAyahNumber;
    this.showLoading();

    try {
      // Load new data
      const data = await this.fetchSegmentsData();
      this.segmentsData = data.segments;
      this.audioData = data.audio;
      this.segmentsLoaded = true;

      this.cleanUpPlayer();

      // Initialize new player
      this.initializeAudioPlayer();

      // Wait for player to be ready
      await new Promise((resolve) => {
        this.player.once('load', resolve);
      });

      // Restore playback position
      if (wasPlaying > 0) {
        const time = this.getAyahStartTimeInSec(currentVerse);
        this.player.seek(time);
        this.updateHighlighting(time * 1000);
        this.player.play();
      }
    } catch (error) {
      console.error('Error changing reciter:', error);
    } finally {
      this.hideLoading();
    }
  }

  // ========== Highlighting Methods ==========
  updateHighlighting(currentTimeMs) {
    const time = currentTimeMs;
    const verse = this.findAyah(time);

    if (!verse) {
      this.clearHighlights();
      return;
    }

    if (!this.currentVerse || this.currentVerse.verse_key !== verse.verse_key) {
      this.highlightVerse(verse);
      this.currentVerse = verse;
      const newAyahNumber = verse.verse_key.split(":")[1];

      if (this.currentAyahNumber !== newAyahNumber) {
        this.currentAyahNumber = newAyahNumber;
        this.updatePlayButtonIcon();
      }
    }

    const word = this.findWord(time, verse);
    if (word && (!this.currentWord || word[0] !== this.currentWord[0])) {
      this.currentWord = word;
      this.highlightWord(word);
    }
  }

  highlightVerse(verse) {
    this.clearHighlights();

    const versesToHighlight = document.querySelectorAll(`[data-ayah="${verse.verse_key}"]`);
    if (versesToHighlight.length > 0) {
      versesToHighlight.forEach(el => {
        el.classList.add('highlighted-verse');
      });

      versesToHighlight[0].scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }

  highlightWord(word) {
    if (!word || !this.currentVerse) return;

    document.querySelectorAll('.highlighted-word').forEach(el => {
      el.classList.remove('highlighted-word');
    });

    const position = word[0];
    const location = `${this.currentVerse.verse_key}:${position}`;

    document.querySelectorAll(`[data-location="${location}"]`).forEach(el => {
      el.classList.add('highlighted-word');
    });
    
    const wordElement = document.querySelector(`[data-location="${location}"]`);
    if (wordElement) {
      const wordData = {
        location: location,
        word: wordElement.dataset.word,
        ayah: this.currentVerse.verse_key,
        type: 'audio_playback',
        timestamp: Date.now()
      };
      notifyParentOfWordHighlight(wordData);
    }
  }

  // ========== Player Initialization ==========
  initializeAudioPlayer() {
    this.player = new Howl({
      src: [this.getAudioUrl()],
      html5: true,
      onplay: () => {
        this.isPlaying = true;
        this.updatePlayButtonIcon();
        this.updateProgress();
      },
      onpause: () => {
        this.pause();
      },
      onend: () => {
        this.isPlaying = false;
        this.updatePlayButtonIcon();
        cancelAnimationFrame(this.rafId);
      },
      onload: () => {
        this.totalDurationEl.textContent = this.formatTime(this.player.duration());
        this.currentTimeEl.classList.remove('hidden');
        this.totalDurationEl.classList.remove('hidden');
      },
      onloaderror: (_, error) => {
        console.error('Audio load error:', error);
        this.hideLoading();
      }
    });
  }



  clearHighlights() {
    document.querySelectorAll('.highlighted-verse, .highlighted-word').forEach(el => {
      el.classList.remove('highlighted-verse', 'highlighted-word');
    });
    this.currentVerse = null;
    this.currentWord = null;
  }

  // ========== Utility Methods ==========
  findAyah(timeMs) {
    if (!this.segmentsData?.length) return null;

    const segments = this.segmentsData;
    let left = 0;
    let right = segments.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const verse = segments[mid];

      if (timeMs >= verse.timestamp_from && timeMs <= verse.timestamp_to) {
        return verse;
      } else if (timeMs < verse.timestamp_from) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    return null;
  }

  findWord(timeMs, verse) {
    if (this.currentWord) {
      // If current word is already set, check if it matches the verse
      if (this.currentWord[1] >= timeMs && this.currentWord[2] <= timeMs) {
        return this.currentWord;
      }
    }

    const segments = verse.segments;
    let left = 0;
    let right = segments.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const word = segments[mid];

      if (timeMs >= word[1] && timeMs <= word[2]) {
        return word;
      } else if (timeMs < word[1]) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    return null;
  }

  formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMinutes = `${minutes}`.padStart(2, '0');
    const paddedSeconds = `${secs}`.padStart(2, '0');

    if (hrs > 0) {
      const paddedHours = `${hrs}`.padStart(2, '0');
      return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      return `${paddedMinutes}:${paddedSeconds}`;
    }
  }


  cleanUpPlayer() {
    if (this.player) {
      this.player.stop();
      this.player.unload();
      this.player = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  getAudioUrl() {
    return this.audioData?.audio_url
  }

  currentChapter() {
    if (this.currentVerse) {
      return this.currentVerse.verse_key.split(":")[0]
    } else {
      return document.querySelector('[data-ayah]').dataset.chapter
    }
  }

  currentReciter() {
    return this.reciterSelect.value;
  }

  async fetchSegmentsData() {
    const response = await fetch(
      `/segments.json?reciter=${this.currentReciter()}&chapter=${this.currentChapter()}`
    );
    if (!response.ok) throw new Error('Failed to load segments data');
    return await response.json();
  }
}